// Switches, segmented controls, scope bars and steppers, as Apple draws them.
import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { Link, NavLink } from 'react-router';
import s from './Controls.module.css';
import { cx } from './cx';
import { MinusIcon, PlusIcon } from './icons';

/** A switch: a checkbox that says it's a switch. Give it a `label`, or name it with `aria-label` or a label around it. */
export function Switch({
  className,
  ref,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> & { ref?: Ref<HTMLInputElement> }) {
  return (
    <span className={cx(s.switch, className)}>
      <input ref={ref} type="checkbox" role="switch" {...rest} />
      <span className={s.track} aria-hidden="true">
        <span className={s.knob} />
      </span>
    </span>
  );
}

/** A labelled switch on a line of its own: the words, then the switch. */
export function SwitchRow({
  label,
  detail,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> & { label: ReactNode; detail?: ReactNode }) {
  const id = useId();
  return (
    <label className={s.switchRow} htmlFor={id}>
      <span>
        <span>{label}</span>
        {detail && <span className={s.switchDetail}>{detail}</span>}
      </span>
      <Switch id={id} {...rest} />
    </label>
  );
}

/**
 * A switch that saves as it's flipped: it shows the new state at once, while it saves, then whatever the server has
 * (the old state again, if the save failed).
 */
export function SavingSwitch({
  checked,
  save,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role' | 'onChange' | 'checked'> & {
  checked: boolean;
  save: (next: boolean) => Promise<unknown>;
}) {
  const [shown, setShown] = useState<boolean | null>(null);
  return (
    <Switch
      checked={shown ?? checked}
      aria-busy={shown !== null || undefined}
      onChange={(e) => {
        const next = e.target.checked;
        setShown(next);
        save(next)
          .catch(() => undefined)
          .finally(() => setShown(null));
      }}
      {...rest}
    />
  );
}

export interface Segment<T extends string> {
  value: T;
  label: ReactNode;
  /** A name for a segment whose label isn't words. */
  accessibleLabel?: string;
}

/**
 * One choice of a few, in a row, with a thumb that slides to it. It's a group of radio buttons: arrow keys move
 * between them, and Tab leaves.
 */
export function SegmentedControl<T extends string>({
  label,
  segments,
  value,
  onChange,
  wide = false,
}: {
  /** What it chooses, for a screen reader. */
  label: string;
  segments: ReadonlyArray<Segment<T>>;
  value: T;
  onChange: (value: T) => void;
  wide?: boolean;
}) {
  const group = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ x: number; w: number } | null>(null);
  useLayoutEffect(() => {
    const root = group.current;
    if (!root) {
      return;
    }
    const place = () => {
      const on = root.querySelector<HTMLElement>('[aria-checked="true"]');
      setThumb(on ? { x: on.offsetLeft - 2, w: on.offsetWidth } : null);
    };
    place();
    const watch = new ResizeObserver(place);
    watch.observe(root);
    return () => watch.disconnect();
  }, [value, segments.length]);
  const move = (e: KeyboardEvent, from: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    const to =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? segments.length - 1
          : step
            ? (from + step + segments.length) % segments.length
            : -1;
    const next = segments[to];
    if (to < 0 || !next) {
      return;
    }
    e.preventDefault();
    onChange(next.value);
    requestAnimationFrame(() => group.current?.querySelectorAll<HTMLElement>('[role="radio"]')[to]?.focus());
  };
  return (
    <div ref={group} role="radiogroup" aria-label={label} className={cx(s.segmented, wide && s.wide)}>
      {thumb && <span className={s.thumb} aria-hidden="true" style={{ translate: `${thumb.x}px 0`, width: thumb.w }} />}
      {segments.map((seg, i) => (
        <button
          key={seg.value}
          type="button"
          role="radio"
          aria-checked={seg.value === value}
          aria-label={seg.accessibleLabel}
          tabIndex={seg.value === value ? 0 : -1}
          className={s.segment}
          onClick={() => onChange(seg.value)}
          onKeyDown={(e) => move(e, i)}
        >
          {seg.label}
        </button>
      ))}
    </div>
  );
}

export interface Scope {
  to: string;
  label: string;
  /** Match only this exact path. */
  end?: boolean;
  /** Whether it's the view shown, when the address's search says so rather than its path. */
  current?: boolean;
}

/** Links across a page's views, drawn as a segmented control: each view has its own address. */
export function ScopeBar({ label, scopes }: { label: string; scopes: readonly Scope[] }) {
  return (
    <nav aria-label={label}>
      <div className={s.scope}>
        {scopes.map((scope) =>
          scope.current === undefined ? (
            <NavLink key={scope.to} to={scope.to} end={scope.end} className={s.scopeLink}>
              {scope.label}
            </NavLink>
          ) : (
            <Link
              key={scope.to}
              to={scope.to}
              aria-current={scope.current ? 'page' : undefined}
              className={s.scopeLink}
            >
              {scope.label}
            </Link>
          ),
        )}
      </div>
    </nav>
  );
}

/** A number changed a step at a time, with − and +. */
export function Stepper({
  label,
  value,
  onChange,
  min = -Infinity,
  max = Infinity,
  step = 1,
  format = String,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  format?: (value: number) => ReactNode;
}) {
  return (
    <div className={s.stepper} role="group" aria-label={label}>
      <output className={s.stepperValue} aria-live="polite">
        {format(value)}
      </output>
      <span className={s.stepperButtons}>
        <button
          type="button"
          aria-label={`Less ${label.toLowerCase()}`}
          disabled={value - step < min}
          onClick={() => onChange(Math.max(min, value - step))}
        >
          <MinusIcon size={18} weight="bold" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={value + step > max}
          onClick={() => onChange(Math.min(max, value + step))}
        >
          <PlusIcon size={18} weight="bold" aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}
